import React from 'react';
import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import { Field } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { PricingPlan } from '@css-xp/graphtypes/src/generated-types-new';
import { SummaryPanelContainer, SummaryPanelRow } from '../../..';
import { PricingTiers } from './pricing-tiers';
import { SummaryGridItem, SummarySection } from '../../../application/src/summary-section/summary-section';

interface Props {
  label: string;
  offeringName?: string | null;
  billingFrequency?: string | null;
  pricingPlan?: PricingPlan | null;
  activeUsers?: number | null;
  productName?: string | null;
  isInvoicePage?: boolean;
  hidePricingCalculatorText?: boolean;
  // hideCurrencyFromValues is used to hide the currency from the values in the pricing tiers table and show it in headers
  hideCurrencyFromValues?: boolean;
  // shouldAlignAmountCellsRight is used to align the cells in th and tr, which show numbers, to the right
  shouldAlignAmountCellsRight?: boolean;
  pricingPlanVersion?: number;
}

const NoData = () => {
  // Importing <NoData/> from @css-xp/ppc-common will create a circular dependency. Hence, creating a new component here.
  return (
    <span data-testid="no-data" aria-label="Not available">
      -
    </span>
  );
};

export const AppliedPricingDetailsTab = ({
  offeringName,
  billingFrequency,
  pricingPlan,
  activeUsers,
  productName,
  isInvoicePage,
  hidePricingCalculatorText,
  hideCurrencyFromValues,
  shouldAlignAmountCellsRight,
  pricingPlanVersion,
}: Props) => {
  const pricingPlanType = capitalize(pricingPlan?.pricingPlanType || '').replace(/_/g, ' ');
  const pricingDetails =
    (offeringName && offeringName.includes(productName as string) ? '' : productName + ' ') +
    (offeringName + ' ' + capitalize(billingFrequency || '') + ' ' + (pricingPlan?.type || ''));
  const nameField =
    (isInvoicePage ? pricingDetails : pricingPlan?.description) +
    ' | ' +
    pricingPlanType +
    ' | ' +
    capitalize(pricingPlan?.status || '');

  const renderPricingPlanDetails = () => {
    const isPricingPlanVersionTwo = pricingPlanVersion === 2;
    const statusValue = capitalize(pricingPlan?.status || '');
    const billingCycleValue = capitalize(billingFrequency || '');
    const activeUsersValue = activeUsers ?? '0';

    if (isPricingPlanVersionTwo) {
      const boxStyles = xcss({
        backgroundColor: 'elevation.surface',
        borderColor: 'color.border',
        borderWidth: 'border.width',
        borderStyle: 'solid',
        borderRadius: 'border.radius.200',
        marginTop: 'space.200',
        marginBottom: 'space.300',
      });
      const pricingEligibilityValue = capitalize(startCase(pricingPlan?.type ?? ''));
      const getNameField = () => {
        let nameField = isInvoicePage ? pricingDetails : pricingPlan?.description;

        if (pricingPlanType) {
          nameField += ' | ' + pricingPlanType;
        }

        if (pricingPlan?.status) {
          nameField += ' | ' + capitalize(pricingPlan.status);
        }

        return nameField;
      };

      return (
        <div>
          <Box paddingInline="space.150" paddingBlock="space.100" xcss={boxStyles}>
            <Box paddingBlockStart="space.100" paddingBlockEnd="space.150">
              <h4>Pricing plan</h4>
            </Box>
            <SummaryPanelContainer version={2}>
              <SummaryPanelRow label="Name" value={getNameField()} useWrapper={true} />
              <SummaryPanelRow label="Plan" value={offeringName || <NoData />} useWrapper={true} />
              <SummaryPanelRow label="Status" value={statusValue || <NoData />} useWrapper={true} />
              <SummaryPanelRow label="Type" value={pricingPlanType || <NoData />} useWrapper={true} />
              <SummaryPanelRow
                label="Pricing eligibility"
                value={pricingEligibilityValue || <NoData />}
                useWrapper={true}
              />
              <SummaryPanelRow label="Billing cycle" value={billingCycleValue || <NoData />} useWrapper={true} />
              <SummaryPanelRow label="Number of users" value={activeUsersValue || <NoData />} useWrapper={true} />
            </SummaryPanelContainer>
          </Box>
        </div>
      );
    }

    return (
      <div className="applied-pricing price-plan-details">
        <h4>Price plan</h4>
        <div className="pricingField">
          <Field label={'Name'} name="units" defaultValue={nameField}>
            {({ fieldProps }) => (
              <Textfield className="unit-field" {...fieldProps} name={'units'} testId="units-field" />
            )}
          </Field>
        </div>

        <SummarySection className="summary-section-internal pricing-summary" columns="1fr 1fr 1fr">
          <SummaryGridItem className="section-item-internal pricing-details">
            <div>
              <label>Plan</label>
              <div>{offeringName}</div>
            </div>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal pricing-details">
            <div>
              <label>Status</label>
              <div>{statusValue}</div>
            </div>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal pricing-details">
            <div>
              <label>Type</label>
              <div>{pricingPlanType}</div>
            </div>
          </SummaryGridItem>
        </SummarySection>
        <SummarySection className="summary-section-internal pricing-summary" columns="1fr 1fr 1fr">
          <SummaryGridItem className="section-item-internal pricing-details">
            <div>
              <label>Billing cycle</label>
              <div>{billingCycleValue}</div>
            </div>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal pricing-details">
            <div>
              <label>Number of users</label>
              <div>{activeUsersValue}</div>
            </div>
          </SummaryGridItem>
        </SummarySection>
      </div>
    );
  };

  return (
    <>
      {renderPricingPlanDetails()}
      <PricingTiers
        pricingPlan={pricingPlan}
        activeUsers={activeUsers}
        isInvoicePage={isInvoicePage}
        hidePricingCalculatorText={hidePricingCalculatorText}
        hideCurrencyFromValues={hideCurrencyFromValues}
        shouldAlignAmountCellsRight={shouldAlignAmountCellsRight}
      />
    </>
  );
};
